import React, { useId, useMemo, useState } from "react";
import { FiChevronDown, FiChevronUp, FiMessageSquare } from "react-icons/fi";

import type { VideoCommentsController } from "../../../Hooks/Comments/Video/useVideoComments";
import { VideoCommentDTO } from "../../../Models/DTOs/VideoCommentsDTO";
import VideoCommentCard from "./VideoCommentCard";

interface VideoCommentThreadProps {
    root: VideoCommentDTO;
    controller: VideoCommentsController;
    onFocusChange?: (focused: boolean) => void;
}

interface VideoCommentReplyNode {
    comment: VideoCommentDTO;
    children: VideoCommentReplyNode[];
}

const VideoCommentThread: React.FC<VideoCommentThreadProps> = ({
    root,
    controller,
    onFocusChange,
}) => {
    const [expanded, setExpanded] = useState(false);
    const repliesId = useId();
    const thread = controller.state.threads[root.id];

    const replyTree = useMemo(() => {
        // The API returns one chronological list, so rebuild the parent/child
        // shape while preserving the order within each sibling group.
        const replies = thread?.items ?? [];
        const commentsById = new Map(
            replies.map((comment) => [comment.id, comment])
        );
        const nodesById = new Map<number, VideoCommentReplyNode>(
            replies.map((comment) => [
                comment.id,
                { comment, children: [] },
            ])
        );
        const roots: VideoCommentReplyNode[] = [];

        const hasValidPathToRoot = (comment: VideoCommentDTO) => {
            let parentId = comment.parent_comment_id;
            const visited = new Set<number>([comment.id]);

            while (parentId !== root.id) {
                if (parentId === null || visited.has(parentId)) return false;
                visited.add(parentId);
                const parent = commentsById.get(parentId);
                if (!parent) return false;
                parentId = parent.parent_comment_id;
            }

            return true;
        };

        replies.forEach((comment) => {
            const node = nodesById.get(comment.id);
            if (!node) return;

            const parentId = comment.parent_comment_id;
            if (
                parentId !== null &&
                parentId !== root.id &&
                hasValidPathToRoot(comment)
            ) {
                const parent = nodesById.get(parentId);
                if (parent) {
                    parent.children.push(node);
                    return;
                }
            }

            roots.push(node);
        });

        return roots;
    }, [root.id, thread?.items]);

    if (root.reply_count <= 0 && !thread?.items.length) return null;

    const visibleReplyCount = Math.max(root.reply_count, thread?.items.length ?? 0);

    const toggle = async () => {
        const next = !expanded;
        setExpanded(next);
        if (next) await controller.ensureReplies(root.id);
    };

    const renderReply = (
        node: VideoCommentReplyNode,
        depth = 1
    ): React.ReactNode => {
        const visualDepth = Math.min(Math.max(depth, 1), 4);
        const hasReplies =
            node.comment.reply_count > 0 || node.children.length > 0;

        return (
            <div
                key={node.comment.id}
                className={`video-comment-thread__node ${
                    hasReplies ? "has-replies" : ""
                }`}
                data-comment-depth={visualDepth}
            >
                <VideoCommentCard
                    comment={node.comment}
                    depth={depth}
                    controller={controller}
                    onFocusChange={onFocusChange}
                />
                {node.children.length > 0 ? (
                    <div className="video-comment-thread__children">
                        {node.children.map((child) =>
                            renderReply(child, depth + 1)
                        )}
                    </div>
                ) : (
                    hasReplies && (
                        <span
                            className="video-comment-thread__connector-tail"
                            aria-hidden="true"
                        />
                    )
                )}
            </div>
        );
    };

    return (
        <div className="video-comment-thread">
            <button
                type="button"
                className="video-comment-thread__toggle"
                aria-expanded={expanded}
                aria-controls={repliesId}
                onClick={() => void toggle()}
            >
                <FiMessageSquare aria-hidden="true" />
                <span>
                    {expanded ? "Hide" : "View"} {visibleReplyCount.toLocaleString()} {" "}
                    {visibleReplyCount === 1 ? "reply" : "replies"}
                </span>
                {expanded ? (
                    <FiChevronUp aria-hidden="true" />
                ) : (
                    <FiChevronDown aria-hidden="true" />
                )}
            </button>

            {expanded && (
                <div id={repliesId} className="video-comment-thread__replies">
                    {thread?.loading && !thread.loaded && (
                        <p className="video-comments__status" role="status">
                            Loading replies…
                        </p>
                    )}
                    {thread?.error && (
                        <div className="video-comments__error" role="alert">
                            <p>{thread.error}</p>
                            <button
                                type="button"
                                onClick={() => void controller.refreshReplies(root.id)}
                            >
                                Try again
                            </button>
                        </div>
                    )}
                    {replyTree.map((node) => renderReply(node))}
                    {thread?.nextCursor && (
                        <button
                            type="button"
                            className="video-comments__load-more"
                            disabled={thread.loadingMore}
                            onClick={() => void controller.loadMoreReplies(root.id)}
                        >
                            {thread.loadingMore ? "Loading…" : "Load more replies"}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default VideoCommentThread;
